/* eslint-disable import/no-named-as-default */
import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Hits, NoHits } from 'searchkit';

// app modules: actions
import { closeTableOfContents } from 'actions/tableOfContents';

// app modules: components
import SearchExamples from 'components/SearchExamples';
import SearchFilters from 'components/SearchFilters';
import SearchResults from 'components/SearchResults';
import NoHitsCustom, { NoHitsCustomError } from 'components/NoHitsCustom';

// app modules: services
import history from 'services/history';

// constants
import { BREAKPOINTS } from 'constants/index';
import { SearchkitContext } from 'components/SearchProvider';

const SearchResultsLayout = React.forwardRef((props, ref) => {
  const { visible } = props;
  const dispatch = useDispatch();
  const bodyWidth = useSelector(state => state.dimensions.body);
  const searchIsActive = useSelector(state => state.search.isActive);
  const searchIsLoading = useSelector(state => state.search.isLoading);
  const searchIsEmpty = useSelector(state => state.search.isEmpty);

  const searchkit = useContext(SearchkitContext);

  useEffect(
    () => {
      // Now that the view is ready we can run the query from the URL
      if (searchkit && history.location.search) {
        searchkit.searchFromUrlQuery(history.location.search);
      }
    },
    [searchkit],
  );

  useEffect(
    () => {
      if (visible) {
        dispatch(closeTableOfContents());
      }
    },
    [visible, dispatch],
  );

  const getPageSize = () => {
    if (bodyWidth <= BREAKPOINTS.DESKTOP) {
      return 20;
    }
    return Math.round(bodyWidth / 245) * 5;
  };

  return (
    <React.Fragment>
      {/* Only renders in wider resolution to get search filters fixed on top of the page on scroll  */}
      {bodyWidth > BREAKPOINTS.PHONE_VERTICAL ? <SearchFilters isVisible={visible} /> : null}
      <div className="layout-header__search-results" ref={ref}>
        {/* Only renders in mobile view to keep search filters scrollable */}
        {bodyWidth <= BREAKPOINTS.PHONE_VERTICAL ? <SearchFilters isVisible={visible} /> : null}
        {!searchIsLoading && searchIsEmpty && visible ? (
          <SearchExamples />
        ) : (
          <React.Fragment>
            <Hits listComponent={SearchResults} hitsPerPage={getPageSize()} />
            <NoHits
              translations={{
                'NoHits.NoResultsFound': 'No results were found',
                'NoHits.DidYouMean': 'Search for {suggestion}',
                'NoHits.SearchWithoutFilters': 'Search for {query} without filters',
              }}
              suggestionsField="title"
              component={NoHitsCustom}
              errorComponent={NoHitsCustomError}
            />
          </React.Fragment>
        )}

        {/* This div is used to anchor tooltips to the search result page so that they scroll with the search results */}
        {searchIsActive ? (
          <div style={{ position: 'relative' }}>
            <div id="search-bound-tooltip" />
          </div>
        ) : null}
      </div>
    </React.Fragment>
  );
});

SearchResultsLayout.propTypes = {
  visible: PropTypes.bool,
};

SearchResultsLayout.defaultProps = {
  visible: false,
};

export default SearchResultsLayout;
